import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Sparkles, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { PORTRAIT_URL, PHONE, PHONE_LINK, TAGLINE } from "@/lib/brand";

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About"
        description="The Velvet Brush — premium makeup artistry in Lucknow. Elegant, warm, artistic. Bridal, engagement and occasion makeup, available for travel."
        path="/about"
      />
      <section className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">{TAGLINE}</p>
          <h1 className="mt-3 font-serif font-bold text-ink text-3xl sm:text-4xl lg:text-5xl">About The Velvet Brush</h1>
          <p className="mt-6 text-ink/70 leading-relaxed">
            The Velvet Brush is a premium makeup artistry studio based in Lucknow, created for one belief — that
            makeup should feel like <em className="font-serif">you</em>, only luminous. We specialise in bridal,
            engagement and occasion makeup, blending timeless elegance with modern glamour.
          </p>
          <p className="mt-4 text-ink/70 leading-relaxed">
            Every face is a canvas with its own story. Our artists take time to understand your features, your outfit
            and your celebration before a single brush touches your skin — so the woman in the mirror is unmistakably you.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Heart, title: "Warm & personal", text: "No accounts, no pressure — just a conversation." },
              { icon: Sparkles, title: "Premium products", text: "Hygiene-first kits and luxury brands." },
              { icon: MapPin, title: "Lucknow & beyond", text: "Available for travel across India." },
            ].map((v) => (
              <div key={v.title} className="bg-white rounded-2xl border border-[#EAE5DF] p-5" data-testid={`value-${v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <v.icon className="w-5 h-5 text-velvet" />
                <h3 className="mt-3 font-serif text-lg text-ink">{v.title}</h3>
                <p className="mt-1 text-xs text-ink/55">{v.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/enquire">
              <Button data-testid="about-enquire-btn" className="w-full sm:w-auto rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold px-8 h-12 active:scale-95 transition-transform">
                Book / Enquire
              </Button>
            </Link>
            <a href={PHONE_LINK}>
              <Button data-testid="about-call-btn" variant="outline" className="w-full sm:w-auto rounded-full border-ink/25 px-8 h-12">
                <Phone className="w-4 h-4 mr-2" /> Call {PHONE}
              </Button>
            </a>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="relative">
          <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-blush/40 via-transparent to-sage/40 -z-10" />
          <img src={PORTRAIT_URL} alt="Founder of The Velvet Brush" data-testid="about-portrait"
            className="w-full rounded-[2rem] object-cover object-top aspect-[4/5] shadow-2xl" />
        </motion.div>
      </section>
    </div>
  );
}
